import { Euro, Package, CheckCircle2, Loader2, Star, Trash2, Mail, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_LABELS } from "@/components/admin/OrdersTable";

export const StatsCards = ({ stats }) => {
  const cards = [
    { icon: Package, label: "Pedidos pagados", value: stats.total_orders, testid: "stat-orders" },
    { icon: Loader2, label: "En curso", value: stats.pending, testid: "stat-pending" },
    { icon: CheckCircle2, label: "Entregados", value: stats.delivered, testid: "stat-delivered" },
    { icon: Euro, label: "Ingresos", value: `${stats.revenue.toFixed(2)} €`, testid: "stat-revenue" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testid}
          className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm flex items-center gap-4">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-cyan-50 text-cyan-600">
            <c.icon className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs text-slate-500">{c.label}</p>
            <p className="font-heading font-bold text-xl text-slate-900">{c.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export const OrdersFilters = ({ search, setSearch, statusFilter, setStatusFilter }) => (
  <div className="flex flex-col sm:flex-row gap-3 mb-4">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        data-testid="orders-search-input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por nº de pedido, cliente, teléfono, email o dirección..."
        className="pl-9 bg-white"
      />
    </div>
    <Select value={statusFilter} onValueChange={setStatusFilter}>
      <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="orders-status-filter">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="todos" data-testid="filter-option-todos">Todos los estados</SelectItem>
        {Object.entries(STATUS_LABELS).map(([value, label]) => (
          <SelectItem key={value} value={value} data-testid={`filter-option-${value}`}>{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const DeleteButton = ({ testId, onClick }) => (
  <Button variant="outline" size="sm" data-testid={testId} onClick={onClick}
    className="rounded-full text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0">
    <Trash2 className="h-4 w-4" />
  </Button>
);

export const ReviewsTab = ({ reviews, onDelete }) => (
  <div className="space-y-3" data-testid="admin-reviews-list">
    {reviews.length === 0 && (
      <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-lg p-6">Todavía no hay reseñas.</p>
    )}
    {reviews.map((r) => (
      <div key={r.id} data-testid={`admin-review-${r.id}`}
        className="bg-white border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-medium text-slate-900">{r.name}</p>
            <span className="inline-flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              ))}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-1">{r.comment}</p>
        </div>
        <DeleteButton testId={`admin-review-delete-${r.id}`} onClick={() => onDelete(r.id)} />
      </div>
    ))}
  </div>
);

export const MessagesTab = ({ messages, onDelete }) => (
  <div className="space-y-3" data-testid="admin-messages-list">
    {messages.length === 0 && (
      <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-lg p-6">No hay mensajes de contacto.</p>
    )}
    {messages.map((m) => (
      <div key={m.id} data-testid={`admin-message-${m.id}`}
        className="bg-white border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="font-medium text-slate-900 flex items-center gap-2">
              <Mail className="h-4 w-4 text-cyan-500" /> {m.name}
            </p>
            <a href={`mailto:${m.email}`} className="text-sm text-cyan-600 hover:underline">{m.email}</a>
            <span className="text-xs text-slate-400">
              {new Date(m.created_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{m.message}</p>
        </div>
        <DeleteButton testId={`admin-message-delete-${m.id}`} onClick={() => onDelete(m.id)} />
      </div>
    ))}
  </div>
);
